import { useEffect, useMemo, useState } from "react";
import {
	Autocomplete,
	AutocompleteCloseReason,
	CircularProgress,
	InputAdornment,
	Typography,
	debounce,
	useTheme
} from "@mui/material";
import { default as TextField } from "@mui/material/TextField/TextField";
import { OnEventApiResponse } from "@origin/platform-types";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { httpService, isOrderType } from "@sal/onevent-portal";
import { Search as SearchIcon } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { SystemName } from "../constants/systemname";

type SearchResult = {
	searchResultID: number | string;
	objectID: number | string;
	objectType: string;
	objectFunction: string;
	title: string;
	description: string;
	dataJSON?: string;
	data?: {
		value?: string;
		statusSystemName?: string;
		containerID?: number;
	};
	searchResultSystemName: string;
	typeCode: string;
};

export function QuickSearch() {
	const navigate = useNavigate();
	const theme = useTheme();
	const queryClient = useQueryClient();

	const [searchTerm, setSearchTerm] = useState<string | undefined>(undefined);

	const [, setInputValue] = useState<string>("");
	const [selectValue, setSelectValue] = useState<SearchResult | null>(null);
	const applySearchTerm = useMemo(() => debounce((value: string | undefined) => setSearchTerm(value), 500), []);

	const queryOptions = useMemo(() => {
		if (searchTerm && searchTerm !== "") {
			return {
				searchTerm: searchTerm,
				searchBy: "PortalContainerTransport"
			};
		}
		return undefined;
	}, [searchTerm]);

	const {
		data,
		isFetching: isLoading,
		refetch
	} = useQuery<OnEventApiResponse<SearchResult[]>, Error>(
		["QuickSearch"],
		({ signal }) => httpService.get("Search", queryOptions, signal),
		{
			keepPreviousData: false,
			enabled: !!searchTerm
		}
	);

	useEffect(() => {
		data?.data.map((d) => {
			if (d.dataJSON) {
				d.data = JSON.parse(d.dataJSON);
			}
		});
	}, [data]);

	useEffect(() => {
		if (queryOptions) {
			refetch();
		}
	}, [queryOptions]);

	useEffect(() => {
		if (searchTerm === "") {
			queryClient.resetQueries({ queryKey: ["QuickSearch"], exact: true });
		}
	}, [searchTerm]);

	const handleOnChange = (e: React.SyntheticEvent<Element, Event>, value: SearchResult | null) => {
		if (value) {
			let url = "#";

			const objectType = value?.objectType;
			const objectID = value?.objectID;
			const containerID = value?.data?.containerID;
			if (containerID) {
				if (isOrderType("Import", objectType)) {
					url = `/${"ImportContainer"}/${containerID}`;
				} else if (isOrderType("Export", objectType)) {
					url = `/${"ExportContainer"}/${containerID}`;
				}
			} else {
				switch (objectType) {
					case SystemName.JobType.ImportFCLTransportOrder:
						url = `/${SystemName.JobType.ImportFCLTransportOrder}/${objectID}`;
						break;
					case SystemName.JobType.ExportFCLTransportOrder:
						url = `/${SystemName.JobType.ExportFCLTransportOrder}/${objectID}`;
						break;
					case SystemName.JobType.ImportFAKTransportOrder:
						url = `/${SystemName.JobType.ImportFAKTransportOrder}/${objectID}`;
						break;
					case SystemName.JobType.ExportFAKTransportOrder:
						url = `/${SystemName.JobType.ExportFAKTransportOrder}/${objectID}`;
						break;
					default:
						break;
				}
			}
			navigate(url);
		}
	};
	const renderOption = (props: React.HTMLAttributes<HTMLLIElement>, option: SearchResult) => {
		return (
			<li {...props} key={`${option.searchResultID}_${option.objectID}`}>
				<Typography fontSize="0.85rem" lineHeight="0.9rem">
					{option.description}
				</Typography>
			</li>
		);
	};

	const getOptionLabel = (option: SearchResult) => {
		return option.data?.value || "";
	};

	const handleOnClose = (event: React.SyntheticEvent, reason: AutocompleteCloseReason) => {
		if (reason === "selectOption") {
			setSelectValue(null);
			setInputValue("");
		}
	};

	return (
		<Autocomplete<SearchResult, false, false, false>
			value={selectValue}
			fullWidth
			clearOnBlur={false}
			loading={isLoading}
			options={data?.data ?? []}
			filterOptions={(options) => options}
			onChange={handleOnChange}
			onClose={handleOnClose}
			onInputChange={(_event, value, reason) => {
				const term = reason === "clear" ? undefined : value;
				setInputValue(value);
				applySearchTerm(term);
			}}
			getOptionLabel={getOptionLabel}
			renderOption={renderOption}
			renderInput={(params) => (
				<TextField
					{...params}
					placeholder={"Quick search"}
					InputProps={{
						...params.InputProps,
						endAdornment: <>{isLoading && <CircularProgress color="inherit" size="1rem" />}</>,
						startAdornment: (
							<InputAdornment position="end" sx={{ color: theme.palette.primary.main }}>
								<SearchIcon />
							</InputAdornment>
						)
					}}
					sx={{
						borderRadius: "24px",
						"& .MuiOutlinedInput-root": {
							"& fieldset": {
								border: "none"
							}
						}
					}}
				/>
			)}
			sx={{
				".MuiAutocomplete-input": {
					padding: "8px 8px !important",
					fontSize: "0.85rem"
				}
			}}
		/>
	);
}
